/**
 * ProPage — /pro pricing page (iter76: Stripe web billing).
 * Monthly/yearly toggle, feature table, payment method badges.
 * CTA → POST /api/billing/checkout → redirect to Stripe Checkout.
 * PRO users see "Manage subscription" → Stripe Billing Portal.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, CreditCard, ShieldCheck, Sparkles } from "lucide-react";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import { ProBetaRequestForm } from "../components/ProBetaRequestForm";
import { API } from "../config";
import { useAuth } from "../AuthContext";

const FEATURES = [
  { name: "Weekly Travel Brief + full news feed", free: true, pro: true },
  { name: "Newsletter + podcast", free: true, pro: true },
  { name: "Listen archive (all past audio briefs)", free: false, pro: true },
  { name: "Weekly Digest themes & deep-dives", free: false, pro: true },
  { name: "Story Clusters (grouped coverage)", free: false, pro: true },
  { name: "Unlimited bookmarks", free: false, pro: true },
  { name: "MCP Server PRO — ask_tourismintel in Claude/Cursor (100 q/mo)", free: false, pro: true },
  { name: "PRO on web AND mobile app (same account)", free: false, pro: true },
];

export default function ProPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plan, setPlan] = useState("yearly");
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/billing/config`).then(r => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/billing/status`).then(r => setStatus(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    const s = searchParams.get("status");
    if (s === "success") toast.success("Payment received — your PRO access is being activated!");
    else if (s === "cancelled") toast.info("Checkout cancelled — no charge was made.");
  }, [searchParams]);

  const startCheckout = async () => {
    if (!user) { navigate("/auth?next=/pro"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/billing/checkout`, { plan });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout — please retry.");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/billing/portal`);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the billing portal.");
      setBusy(false);
    }
  };

  const isPro = status?.is_pro;
  const enabled = config?.enabled;

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#191c1d]" data-testid="pro-page">
      <PublicPageNav active="pro" />
      <main className="max-w-4xl mx-auto px-6 pt-14 pb-24">
        <p className="category-tag text-[#006874] mb-4">TOURISMINTEL PRO</p>
        <h1 className="editorial-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-4">
          The full travel-intelligence stack.
        </h1>
        <p className="text-base text-[#6b7a7d] max-w-xl mb-10">
          One subscription, everywhere: PRO unlocks the complete archive, themed digests
          and story clusters on the web and in the mobile app — same account, same access.
        </p>

        {isPro ? (
          <div className="rounded-2xl border border-[#006874]/20 bg-[#006874]/5 p-8 mb-12" data-testid="pro-active-panel">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-[#006874]" />
              <p className="font-semibold text-lg">You&apos;re PRO</p>
            </div>
            <p className="text-sm text-[#6b7a7d] mb-5">
              Source: {status?.pro_source || "—"}{status?.pro_until ? ` · renews/expires ${new Date(status.pro_until).toLocaleDateString()}` : ""}
            </p>
            {status?.has_billing_profile && (
              <button data-testid="manage-subscription-btn" onClick={openPortal} disabled={busy}
                className="px-6 py-3 rounded-full bg-[#006874] text-white text-sm font-medium hover:bg-[#00525c] transition-colors disabled:opacity-50">
                Manage subscription
              </button>
            )}
          </div>
        ) : (
          <div className="mb-12">
            {enabled === false ? (
              <div className="max-w-xl" data-testid="billing-coming-soon">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#7c3aed]/10 text-[#7c3aed] text-xs font-bold uppercase tracking-wider mb-4">
                  <Sparkles className="w-3.5 h-3.5" /> Web checkout — coming soon
                </span>
                <p className="text-sm text-[#6b7a7d] mb-6" data-testid="billing-disabled-note">
                  Online payments are launching shortly. Meanwhile, we grant <strong>Beta PRO access manually</strong> —
                  request it below and we&apos;ll get back to you by email.
                </p>
                <ProBetaRequestForm dark={false} />
              </div>
            ) : (
              <>
                <div className="inline-flex rounded-full bg-[#eef0f0] p-1 mb-6" data-testid="plan-toggle">
                  {["monthly", "yearly"].map(p => (
                    <button key={p} data-testid={`plan-toggle-${p}`} onClick={() => setPlan(p)}
                      className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${plan === p ? "bg-white shadow text-[#191c1d]" : "text-[#6b7a7d]"}`}>
                      {p === "monthly" ? "Monthly" : "Yearly"}{p === "yearly" && <span className="ml-1.5 text-xs text-[#006874]">save 2 months</span>}
                    </button>
                  ))}
                </div>
                <div>
                  <button data-testid="upgrade-cta" onClick={startCheckout} disabled={busy}
                    className="px-8 py-4 rounded-full bg-[#006874] text-white font-medium hover:bg-[#00525c] transition-colors disabled:opacity-50">
                    {busy ? "Redirecting…" : user ? `Upgrade to PRO — ${plan}` : "Sign in to upgrade"}
                  </button>
                </div>
                <div className="flex items-center gap-4 mt-5 text-[#6b7a7d]">
                  <span className="flex items-center gap-1.5 text-xs"><CreditCard className="w-4 h-4" />Cards · Apple Pay · Google Pay</span>
                  <span className="flex items-center gap-1.5 text-xs"><ShieldCheck className="w-4 h-4" />PayPal</span>
                  <span className="text-xs">Secure checkout by Stripe</span>
                </div>
              </>
            )}
          </div>
        )}

        <div className="rounded-2xl border border-[#e5e7e7] overflow-hidden" data-testid="feature-table">
          <div className="grid grid-cols-[1fr_80px_80px] bg-[#f3f4f5] px-6 py-3 text-xs font-semibold uppercase tracking-wide text-[#6b7a7d]">
            <span>Feature</span><span className="text-center">Free</span><span className="text-center text-[#006874]">PRO</span>
          </div>
          {FEATURES.map(f => (
            <div key={f.name} className="grid grid-cols-[1fr_80px_80px] px-6 py-3.5 border-t border-[#eef0f0] items-center">
              <span className="text-sm">{f.name}</span>
              <span className="text-center">{f.free ? <Check className="w-4 h-4 inline text-[#6b7a7d]" /> : <span className="text-[#c3c9c9]">—</span>}</span>
              <span className="text-center">{f.pro ? <Check className="w-4 h-4 inline text-[#006874]" /> : <span className="text-[#c3c9c9]">—</span>}</span>
            </div>
          ))}
        </div>

        <p className="text-xs text-[#9aa5a6] mt-8">
          Prices shown at checkout. Subscriptions renew automatically and can be cancelled
          anytime from the billing portal. Mobile purchases are managed via the App Store / Play Store.
        </p>
      </main>
      <PublicPageFooter />
    </div>
  );
}
